'use client';

import { useMemo } from 'react';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { HeatmapRect } from '@visx/heatmap';
import { Text } from '@visx/text';
import type { StateTransitionMatrix } from '@/types/markov.types';

interface StateMatrixProps {
  matrix: StateTransitionMatrix;
  width?: number;
  height?: number;
}

interface BinData {
  bin: number;
  bins: {
    bin: number;
    count: number;
    probability: number;
  }[];
}

export function StateMatrix({ matrix, width = 600, height = 600 }: StateMatrixProps) {
  const margin = { top: 80, right: 20, bottom: 40, left: 80 };
  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  // Transform matrix into heatmap data format
  const heatmapData: BinData[] = useMemo(() => {
    return matrix.matrix.map((row, fromState) => ({
      bin: fromState,
      bins: row.map((probability, toState) => ({
        bin: toState,
        count: probability,
        probability: probability,
      })),
    }));
  }, [matrix.matrix]);

  // Get min/max for color scale
  const { min, max } = useMemo(() => {
    const allValues = matrix.matrix.flat();
    return {
      min: Math.min(...allValues),
      max: Math.max(...allValues),
    };
  }, [matrix.matrix]);

  // Color scale - blue to red
  const colorScale = useMemo(
    () =>
      scaleLinear<string>({
        domain: [min, max],
        range: ['#f0f9ff', '#1e40af'], // light blue to dark blue
      }),
    [min, max]
  );

  const binWidth = xMax / matrix.matrix.length;
  const binHeight = yMax / matrix.matrix.length;

  return (
    <div className="relative">
      <svg width={width} height={height}>
        <Group top={margin.top} left={margin.left}>
          <HeatmapRect
            data={heatmapData}
            xScale={(d) => d * binWidth}
            yScale={(d) => d * binHeight}
            colorScale={colorScale}
            binWidth={binWidth}
            binHeight={binHeight}
            gap={2}
          >
            {(heatmap) =>
              heatmap.map((bins) =>
                bins.map((bin) => {
                  const probability = bin.datum.probability;
                  const isHighProbability = probability > 0.3;

                  return (
                    <g key={`heatmap-rect-${bin.row}-${bin.column}`}>
                      <rect
                        className="transition-opacity hover:opacity-80 cursor-pointer"
                        x={bin.x}
                        y={bin.y}
                        width={bin.width}
                        height={bin.height}
                        fill={bin.color}
                        rx={2}
                      />
                      {/* Show probability text if significant */}
                      {binWidth > 40 && probability > 0.05 && (
                        <Text
                          x={bin.x + bin.width / 2}
                          y={bin.y + bin.height / 2}
                          fontSize={Math.min(binWidth / 4, 12)}
                          textAnchor="middle"
                          verticalAnchor="middle"
                          fill={isHighProbability ? 'white' : '#1f2937'}
                          fontWeight="bold"
                        >
                          {(probability * 100).toFixed(0)}%
                        </Text>
                      )}
                    </g>
                  );
                })
              )
            }
          </HeatmapRect>

          {/* Y-axis labels (From State) */}
          {matrix.state_labels.map((label, i) => (
            <Text
              key={`y-label-${i}`}
              x={-10}
              y={i * binHeight + binHeight / 2}
              fontSize={12}
              textAnchor="end"
              verticalAnchor="middle"
              fontWeight="semibold"
            >
              {label}
            </Text>
          ))}

          {/* X-axis labels (To State) */}
          {matrix.state_labels.map((label, i) => (
            <Text
              key={`x-label-${i}`}
              x={i * binWidth + binWidth / 2}
              y={yMax + 25}
              fontSize={12}
              textAnchor="middle"
              fontWeight="semibold"
            >
              {label}
            </Text>
          ))}
        </Group>

        {/* Title */}
        <Text
          x={width / 2}
          y={30}
          fontSize={16}
          textAnchor="middle"
          fontWeight="bold"
        >
          State Transition Matrix
        </Text>

        {/* Y-axis title */}
        <Text
          x={20}
          y={height / 2}
          fontSize={14}
          textAnchor="middle"
          fontWeight="semibold"
          angle={-90}
        >
          From State
        </Text>

        {/* X-axis title */}
        <Text
          x={width / 2}
          y={height - 10}
          fontSize={14}
          textAnchor="middle"
          fontWeight="semibold"
        >
          To State
        </Text>
      </svg>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center gap-4">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f0f9ff' }} />
          <span className="text-sm">Low ({(min * 100).toFixed(0)}%)</span>
        </div>
        <div className="h-4 w-32 rounded" style={{
          background: 'linear-gradient(to right, #f0f9ff, #1e40af)'
        }} />
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#1e40af' }} />
          <span className="text-sm">High ({(max * 100).toFixed(0)}%)</span>
        </div>
      </div>

      {/* Metadata */}
      <div className="mt-4 text-center text-sm text-muted-foreground">
        <div>Total Transitions: {matrix.total_transitions.toLocaleString()}</div>
        <div>
          Training Period: {new Date(matrix.training_period.start).toLocaleDateString()} - {new Date(matrix.training_period.end).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}
